import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  ArrowBack,
  OpenInNew,
  Warning,
  CheckCircle,
  AccessTime,
  CalendarToday,
  Person,
  LinkedIn,
} from '@mui/icons-material';
import SEOHead from '../../components/SEO/SEOHead';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import ArticleTable from '../../components/Blog/ArticleTable';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import ReferralCTA from '../../components/shared/ReferralCTA';
import nataliaPhoto from '../../assets/img/author-natalia.jpg';
import { useTranslation } from 'react-i18next';

const HERO_IMAGE = '/images/blog/tfsa-rrsp-fhsa-comparacion-ahorro.jpg';
const AUTHOR = { name: 'Natalia Duarte', linkedin: 'https://www.linkedin.com/in/natalia-duarteh/', photo: nataliaPhoto };

const TFSAvsRRSPvsFHSAPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const p = 'blog.tfsaVsRrspVsFhsa';

  const TAGS = [t(`${p}.tag1`), t(`${p}.tag2`), t(`${p}.tag3`)];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(`${p}.seoTitle`),
    description: t(`${p}.seoDescription`),
    datePublished: '2026-02-24',
    dateModified: '2026-03-09',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
      logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
    },
    mainEntityOfPage: 'https://www.finmaple.com/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-headline', '.atomic-answer'],
    },
    inLanguage: i18n.language,
  };

  const COMPARISON_ROWS = [
    { feature: t(`${p}.tableFeature1`), tfsa: t(`${p}.tableTfsa1`), rrsp: t(`${p}.tableRrsp1`), fhsa: t(`${p}.tableFhsa1`) },
    { feature: t(`${p}.tableFeature2`), tfsa: t(`${p}.tableTfsa2`), rrsp: t(`${p}.tableRrsp2`), fhsa: t(`${p}.tableFhsa2`) },
    { feature: t(`${p}.tableFeature3`), tfsa: t(`${p}.tableTfsa3`), rrsp: t(`${p}.tableRrsp3`), fhsa: t(`${p}.tableFhsa3`) },
    { feature: t(`${p}.tableFeature4`), tfsa: t(`${p}.tableTfsa4`), rrsp: t(`${p}.tableRrsp4`), fhsa: t(`${p}.tableFhsa4`) },
    { feature: t(`${p}.tableFeature5`), tfsa: t(`${p}.tableTfsa5`), rrsp: t(`${p}.tableRrsp5`), fhsa: t(`${p}.tableFhsa5`) },
    { feature: t(`${p}.tableFeature6`), tfsa: t(`${p}.tableTfsa6`), rrsp: t(`${p}.tableRrsp6`), fhsa: t(`${p}.tableFhsa6`) },
    { feature: t(`${p}.tableFeature7`), tfsa: t(`${p}.tableTfsa7`), rrsp: t(`${p}.tableRrsp7`), fhsa: t(`${p}.tableFhsa7`) },
    { feature: t(`${p}.tableFeature8`), tfsa: t(`${p}.tableTfsa8`), rrsp: t(`${p}.tableRrsp8`), fhsa: t(`${p}.tableFhsa8`) },
    { feature: t(`${p}.tableFeature9`), tfsa: t(`${p}.tableTfsa9`), rrsp: t(`${p}.tableRrsp9`), fhsa: t(`${p}.tableFhsa9`) },
    { feature: t(`${p}.tableFeature10`), tfsa: t(`${p}.tableTfsa10`), rrsp: t(`${p}.tableRrsp10`), fhsa: t(`${p}.tableFhsa10`) }
  ];

  const FAQS = [
    { question: t(`${p}.faq1Q`), answer: t(`${p}.faq1A`) },
    { question: t(`${p}.faq2Q`), answer: t(`${p}.faq2A`) },
    { question: t(`${p}.faq3Q`), answer: t(`${p}.faq3A`) },
    { question: t(`${p}.faq4Q`), answer: t(`${p}.faq4A`) },
    { question: t(`${p}.faq5Q`), answer: t(`${p}.faq5A`) }
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t(`${p}.seoTitle`)}
        description={t(`${p}.seoDescription`)}
        canonical="/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes"
        routeKey="blogTFSA"
        seoKey="blogTFSA"
        noIndex={i18n.language !== 'es'}
        ogImage={`https://www.finmaple.com${HERO_IMAGE}`}
        schema={articleSchema}
      />

      {/* Hero Image */}
      <Box
        role="img"
        aria-label={t(`${p}.heroAlt`)}
        sx={{
          width: '100%',
          height: { xs: 220, md: 360 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            {t(`${p}.backToGuides`)}
          </Button>
        </Container>
      </Box>

      <TrustStrip />

      {/* Article Content */}
      <Container maxWidth="md" sx={{ mt: 0, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: t(`${p}.breadcrumbGuides`), to: `/${lng}/blog` }, { label: t(`${p}.breadcrumbCurrent`) }]} />
          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TAGS.map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                sx={{
                  bgcolor: '#F0F2F5',
                  color: '#5F6B7A',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                }}
              />
            ))}
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {t(`${p}.title`)}
          </Typography>

          {/* Meta */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              color: '#5F6B7A',
              mb: 3,
              fontSize: '0.9rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <a
                href={AUTHOR.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>
                  {AUTHOR.name}
                </Typography>
                <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t(`${p}.date`)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t(`${p}.readTime`)}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

        {/* Atomic Answer */}
        <AtomicAnswer text={t(`${p}.atomicAnswer`)} />

        {/* Key Takeaways - Collapsible */}
        <Box
          sx={{
            border: '1px solid #EEF0F3',
            borderRadius: '10px',
            overflow: 'hidden',
            mt: 5,
            mb: 5,
          }}
        >
          <Box
            onClick={() => setTakeawaysOpen(!takeawaysOpen)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              bgcolor: '#F7F9FC',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#EEEEEE' },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
              {t(`${p}.takeawaysTitle`)}
            </Typography>
            {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
          </Box>
          <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
              <li>{t(`${p}.takeaway1`)}</li>
              <li>{t(`${p}.takeaway2`)}</li>
              <li>{t(`${p}.takeaway3`)}</li>
              <li>{t(`${p}.takeaway4`)}</li>
              <li>{t(`${p}.takeaway5`)}</li>
            </Box>
        </Box>

        {/* H2: Estatus fiscal */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t(`${p}.h2FiscalStatus`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fiscalStatusP1a`)}
          <strong>{t(`${p}.fiscalStatusP1Bold`)}</strong>
          {t(`${p}.fiscalStatusP1b`)}
          <RouterLink to={`/${lng}/blog/guia-impuestos-latinos-quebec-2026`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.fiscalStatusP1Link`)}</RouterLink>
          {t(`${p}.fiscalStatusP1c`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fiscalStatusP2a`)}<strong>{t(`${p}.fiscalStatusP2Bold`)}</strong>{t(`${p}.fiscalStatusP2b`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fiscalStatusP3`)}
        </Typography>

        {/* Warning Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            bgcolor: '#FFFBF0',
            border: '1px solid #FFD54F',
            borderRadius: '10px',
            p: 3,
            mb: 4,
          }}
        >
          <Warning sx={{ color: '#F9A825', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
              {t(`${p}.warningBeforeContribTitle`)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              {t(`${p}.warningBeforeContribText`)}
            </Typography>
          </Box>
        </Box>

        {/* H2: TFSA */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t(`${p}.h2Tfsa`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.tfsaP1a`)}<strong>{t(`${p}.tfsaP1Bold1`)}</strong>{t(`${p}.tfsaP1b`)}<strong>{t(`${p}.tfsaP1Bold2`)}</strong>{t(`${p}.tfsaP1c`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.tfsaP2a`)}<strong>{t(`${p}.tfsaP2Bold`)}</strong>{t(`${p}.tfsaP2b`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          <strong>{t(`${p}.tfsaP3Bold`)}</strong>{t(`${p}.tfsaP3`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          <strong>{t(`${p}.tfsaP4Bold`)}</strong>{t(`${p}.tfsaP4`)}
        </Typography>

        {/* TFSA Best For Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#F0FAF1',
            borderRadius: '10px',
            p: 2.5,
            mb: 4,
          }}
        >
          <CheckCircle sx={{ color: '#2E7D32', mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#1B5E20', lineHeight: 1.7 }}>
            <strong>{t(`${p}.tfsaIdealBold`)}</strong>{t(`${p}.tfsaIdealText`)}
          </Typography>
        </Box>

        {/* H2: RRSP */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t(`${p}.h2Rrsp`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.rrspP1a`)}<strong>{t(`${p}.rrspP1Bold`)}</strong>{t(`${p}.rrspP1b`)}<strong>{t(`${p}.rrspP1Bold2`)}</strong>{t(`${p}.rrspP1c`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.rrspP2a`)}
          <strong>{t(`${p}.rrspP2Bold`)}</strong>
          {t(`${p}.rrspP2b`)}
          <RouterLink to={`/${lng}/blog/rrsp-deadline-marzo-2026`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.rrspP2Link`)}</RouterLink>
          {t(`${p}.rrspP2c`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          <strong>{t(`${p}.rrspP3Bold`)}</strong>{t(`${p}.rrspP3`)}
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
          <li><strong>{t(`${p}.rrspHbpBold`)}</strong>{t(`${p}.rrspHbpText`)}</li>
          <li><strong>{t(`${p}.rrspLlpBold`)}</strong>{t(`${p}.rrspLlpText`)}</li>
        </Box>

        {/* RRSP Best For Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#F7F9FC',
            borderRadius: '10px',
            p: 2.5,
            mb: 4,
          }}
        >
          <CheckCircle sx={{ color: '#1E3A5F', mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#0D47A1', lineHeight: 1.7 }}>
            <strong>{t(`${p}.rrspIdealBold`)}</strong>{t(`${p}.rrspIdealText`)}
          </Typography>
        </Box>

        {/* H2: FHSA */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t(`${p}.h2Fhsa`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fhsaP1a`)}<strong>{t(`${p}.fhsaP1Bold`)}</strong>{t(`${p}.fhsaP1b`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fhsaP2a`)}<strong>{t(`${p}.fhsaP2Bold1`)}</strong>{t(`${p}.fhsaP2b`)}<strong>{t(`${p}.fhsaP2Bold2`)}</strong>{t(`${p}.fhsaP2c`)}<strong>{t(`${p}.fhsaP2Bold3`)}</strong>{t(`${p}.fhsaP2d`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fhsaP3a`)}
          <strong>{t(`${p}.fhsaP3Bold`)}</strong>
          {t(`${p}.fhsaP3b`)}
          <RouterLink to={`/${lng}/blog/primera-hipoteca-canada-guia`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.fhsaP3Link`)}</RouterLink>
          {t(`${p}.fhsaP3c`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fhsaP4`)}
        </Typography>

        {/* FHSA Best For Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#FFFBF0',
            borderRadius: '10px',
            p: 2.5,
            mb: 4,
          }}
        >
          <CheckCircle sx={{ color: '#7D5A00', mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#BF360C', lineHeight: 1.7 }}>
            <strong>{t(`${p}.fhsaIdealBold`)}</strong>{t(`${p}.fhsaIdealText`)}
          </Typography>
        </Box>

        {/* H2: Arbol de decision */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t(`${p}.h2DecisionTree`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
          {t(`${p}.decisionTreeIntro`)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3, fontStyle: 'italic' }}>
          {t(`${p}.h2DecisionTreeSub`)}
        </Typography>

        {/* Decision Flow */}
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Step 1 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 36 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#1E3A5F', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>1</Box>
              <Box sx={{ width: 2, flex: 1, bgcolor: '#D0D5DD', my: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1, pb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                {t(`${p}.q1Title`)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 180, bgcolor: '#F0FAF1', borderRadius: '8px', p: 2, border: '1px solid #B8E0BA' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E7D32', mb: 0.5 }}>{t(`${p}.yes`)}</Typography>
                  <Typography variant="body2" sx={{ color: '#1B5E20', lineHeight: 1.6 }}>{t(`${p}.q1Yes`)}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 180, bgcolor: '#F7F9FC', borderRadius: '8px', p: 2, border: '1px solid #D0D5DD' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#5F6B7A', mb: 0.5 }}>{t(`${p}.no`)}</Typography>
                  <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6 }}>{t(`${p}.nextQuestion`)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Step 2 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 36 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#1E3A5F', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>2</Box>
              <Box sx={{ width: 2, flex: 1, bgcolor: '#D0D5DD', my: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1, pb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                {t(`${p}.q2Title`)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 180, bgcolor: '#F0FAF1', borderRadius: '8px', p: 2, border: '1px solid #B8E0BA' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E7D32', mb: 0.5 }}>{t(`${p}.yes`)}</Typography>
                  <Typography variant="body2" sx={{ color: '#1B5E20', lineHeight: 1.6 }}>{t(`${p}.q2Yes`)}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 180, bgcolor: '#F7F9FC', borderRadius: '8px', p: 2, border: '1px solid #D0D5DD' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#5F6B7A', mb: 0.5 }}>{t(`${p}.no`)}</Typography>
                  <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6 }}>{t(`${p}.nextQuestion`)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Step 3 */}
          <Box sx={{ display: 'flex', gap: 2, mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 36 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#1E3A5F', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>3</Box>
              <Box sx={{ width: 2, flex: 1, bgcolor: '#D0D5DD', my: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1, pb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                {t(`${p}.q3Title`)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 180, bgcolor: '#F0FAF1', borderRadius: '8px', p: 2, border: '1px solid #B8E0BA' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E7D32', mb: 0.5 }}>{t(`${p}.yes`)}</Typography>
                  <Typography variant="body2" sx={{ color: '#1B5E20', lineHeight: 1.6 }}>{t(`${p}.q3Yes`)}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 180, bgcolor: '#F7F9FC', borderRadius: '8px', p: 2, border: '1px solid #D0D5DD' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#5F6B7A', mb: 0.5 }}>{t(`${p}.no`)}</Typography>
                  <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6 }}>{t(`${p}.defaultOption`)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Default - TFSA */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 36 }}>
              <CheckCircle sx={{ color: '#1E3A5F', fontSize: 36 }} />
            </Box>
            <Box sx={{ flex: 1, bgcolor: '#E8F5E9', borderRadius: '10px', p: 2.5, border: '1px solid #A5D6A7' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
                {t(`${p}.defaultTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#1A3152', lineHeight: 1.6 }}>
                {t(`${p}.defaultTextA`)}
                <RouterLink to={`/${lng}/blog/mejores-cuentas-sin-fees-recien-llegados`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t(`${p}.defaultLink`)}</RouterLink>
                {t(`${p}.defaultTextB`)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Why this order */}
        <Box sx={{ bgcolor: '#F7F9FC', border: '1px solid #EEF0F3', borderRadius: '10px', p: 3, mb: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 1 }}>
            {t(`${p}.whyThisOrderTitle`)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
            {t(`${p}.whyThisOrderText`)}
          </Typography>
        </Box>

        {/* Comparison Table */}
        <Typography variant="h4" sx={{ color: '#1A1A1A', mt: 4, mb: 2 }}>
          {t(`${p}.comparisonTableTitle`)}
        </Typography>
        <ArticleTable
          columns={[
            { key: 'feature', label: t(`${p}.colFeature`) },
            { key: 'tfsa', label: 'TFSA' },
            { key: 'rrsp', label: 'RRSP' },
            { key: 'fhsa', label: 'FHSA' },
          ]}
          rows={COMPARISON_ROWS}
        />

        {/* H2: Contribution room */}
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
          {t(`${p}.h2ContribRoom`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.contribRoomIntro`)}
        </Typography>

        {/* TFSA Room */}
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E3A5F', mt: 3, mb: 1 }}>
          {t(`${p}.tfsaRoomTitle`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.tfsaRoomP1a`)}<strong>{t(`${p}.tfsaRoomP1Bold`)}</strong>{t(`${p}.tfsaRoomP1b`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          <strong>{t(`${p}.tfsaRoomPenaltyBold`)}</strong>{t(`${p}.tfsaRoomPenaltyText`)}
        </Typography>

        {/* FHSA Room */}
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E3A5F', mt: 3, mb: 1 }}>
          {t(`${p}.fhsaRoomTitle`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.fhsaRoomP1a`)}<strong>{t(`${p}.fhsaRoomP1Bold`)}</strong>{t(`${p}.fhsaRoomP1b`)}
        </Typography>

        {/* RRSP Room */}
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E3A5F', mt: 3, mb: 1 }}>
          {t(`${p}.rrspRoomTitle`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
          {t(`${p}.rrspRoomP1`)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
          <strong>{t(`${p}.rrspRoomPenaltyBold`)}</strong>{t(`${p}.rrspRoomPenaltyText`)}
        </Typography>

        {/* Warning Box - Over-contribution */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            bgcolor: '#FFEBEE',
            border: '1px solid #EF9A9A',
            borderRadius: '10px',
            p: 3,
            mb: 4,
          }}
        >
          <Warning sx={{ color: '#C62828', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
              {t(`${p}.overContribWarningTitle`)}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, '& li': { color: '#1A1A1A', lineHeight: 1.7, mb: 0.5 } }}>
              <li><strong>TFSA:</strong> {t(`${p}.overContribTfsa`)}</li>
              <li><strong>RRSP:</strong> {t(`${p}.overContribRrsp`)}</li>
              <li><strong>FHSA:</strong> {t(`${p}.overContribFhsa`)}</li>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mt: 5, mb: 0 }} />

        {/* FAQ Section */}
        <FAQSection faqs={FAQS} />


        {/* Affiliate Widget */}
        <Box sx={{ my: 5 }}>
          <Typography variant="h4" sx={{ color: '#1A1A1A', mb: 3 }}>
            {t(`${p}.whereToOpenTitle`)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 250,
                border: '1px solid #E6E9EE',
                borderRadius: '10px',
                p: 3,
                bgcolor: '#fff',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                Wealthsimple
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 2 }}>
                {t(`${p}.wealthsimpleDesc`)}
              </Typography>
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: '#1E3A5F',
                  color: '#fff',
                  px: 2,
                  py: 0.75,
                  borderRadius: '14px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {t(`${p}.recommendedByFinmaple`)}
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 250,
                border: '1px solid #E6E9EE',
                borderRadius: '10px',
                p: 3,
                bgcolor: '#fff',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                Questrade
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 2 }}>
                {t(`${p}.questradeDesc`)}
              </Typography>
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: '#7D5A00',
                  color: '#fff',
                  px: 2,
                  py: 0.75,
                  borderRadius: '14px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {t(`${p}.noCommissionEtfs`)}
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 2, mb: 3 }}>
            <ReferralCTA bank="wealthsimple">{t(`${p}.openWealthsimpleCta`)}</ReferralCTA>
          </Box>
        </Box>

        {/* Interlink CTA Box */}
        <Box
          sx={{
            bgcolor: '#1E3A5F',
            borderRadius: '10px',
            p: 3,
            mt: 4,
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
              {t(`${p}.rrspCtaTitle`)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
              {t(`${p}.rrspCtaSubtitle`)}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={`/${lng}/blog/rrsp-deadline-marzo-2026`}
            variant="contained"
            endIcon={<ArrowForward />}
            sx={{
              bgcolor: '#fff',
              color: '#1E3A5F',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '14px',
              px: 3,
              flexShrink: 0,
              '&:hover': { bgcolor: '#E8EAF6' },
            }}
          >
            {t(`${p}.rrspCtaButton`)}
          </Button>
        </Box>

        {/* Secondary CTA - Comparador */}
        <Box
          sx={{
            border: '1px solid #EEF0F3',
            borderRadius: '10px',
            p: 3,
            mt: 2,
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ color: '#1A1A1A', fontWeight: 600, mb: 0.5 }}>
              {t(`${p}.secondaryCtaTitle`)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6 }}>
              {t(`${p}.secondaryCtaText`)}
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={`/${lng}/cuentas-registradas`}
            variant="outlined"
            endIcon={<ArrowForward />}
            sx={{
              color: '#1E3A5F',
              borderColor: '#1E3A5F',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '14px',
              px: 3,
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(30,58,95,0.04)', borderColor: '#1A3152' },
            }}
          >
            {t(`${p}.secondaryCtaButton`)}
          </Button>
        </Box>

        {/* Official Sources */}
        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
          <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
            {t(`${p}.officialSources`)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Link href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              CRA: Tax-Free Savings Account (TFSA) <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Link href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              CRA: Registered Retirement Savings Plans (RRSPs) <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Link href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              CRA: First Home Savings Account (FHSA) <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Link href="https://www.canada.ca/en/revenue-agency/services/tax/international-non-residents/individuals-leaving-entering-canada-non-residents/newcomers-canada-immigrants.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              CRA: Newcomers to Canada (Immigrants) <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
          </Box>
          <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
            {t(`${p}.lastUpdated`)}
          </Typography>
        </Box>

          {/* Author Bio */}
          <Divider sx={{ mt: 5, mb: 0 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              p: 3,
              bgcolor: '#F7F9FC',
              borderRadius: '12px',
              mt: 3,
            }}
          >
            <Box
              component="img"
              src={AUTHOR.photo}
              alt={AUTHOR.name}
              loading="lazy"
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                {AUTHOR.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                {t(`${p}.authorRole`)}
              </Typography>
            </Box>
            <Button
              href={AUTHOR.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<LinkedIn />}
              size="small"
              sx={{
                textTransform: 'none',
                color: '#1E3A5F',
                fontWeight: 600,
                borderColor: '#1E3A5F',
                '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' },
              }}
              variant="outlined"
            >
              LinkedIn
            </Button>
          </Box>
        </Box>

        <RelatedArticles slug="tfsa-vs-rrsp-vs-fhsa-inmigrantes" />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default TFSAvsRRSPvsFHSAPage;
